// Phase OP — slice 6. Founder-only odds-health observability.
//
// Honest by construction: every number rendered comes from
// odds_health_snapshot() which counts real DB rows. No simulation,
// no fabrication. Credit floor / warn thresholds shown verbatim
// from the server payload — UI cannot decide its own thresholds.
// Spec §10 escalation conditions are surfaced as the open
// escalations list.

import { useOddsHealth } from "../../sportCoverage/useOddsHealth.ts";

const card: React.CSSProperties = {
  border: "1px solid var(--sa-hairline, #2a2a30)",
  borderRadius: 6,
  padding: 16,
  display: "flex",
  flexDirection: "column",
  gap: 10,
};
const eyebrow: React.CSSProperties = {
  fontFamily: "var(--sa-font-mono, monospace)",
  fontSize: 11,
  letterSpacing: "0.16em",
  textTransform: "uppercase",
  color: "var(--sa-fg-muted, #9a9aa2)",
};
const cellMono: React.CSSProperties = {
  fontFamily: "var(--sa-font-mono, monospace)",
  fontSize: 12,
};

function fmtTs(s: string | null): string {
  if (!s) return "—";
  const t = new Date(s);
  if (Number.isNaN(t.getTime())) return s;
  return t.toISOString().replace("T", " ").slice(0, 19) + "Z";
}

function CreditBadge({ remaining, floor, warn }: { remaining: number | null; floor: number; warn: number }): JSX.Element {
  const c =
    remaining === null
      ? "var(--sa-fg-muted, #9a9aa2)"
      : remaining < floor
      ? "var(--sa-accent-negative, #d24747)"
      : remaining < warn
      ? "var(--sa-accent-saffron, #e0a106)"
      : "var(--sa-accent-positive, #1fbf75)";
  return (
    <span style={{ fontSize: 22, color: c, ...cellMono }}>
      {remaining === null ? "—" : `${remaining} / 500`}
    </span>
  );
}

export function OddsHealthPage(): JSX.Element {
  const { snapshot, loading, error, refetch } = useOddsHealth();

  return (
    <div
      style={{ padding: "20px 24px", maxWidth: 1100, margin: "0 auto", display: "flex", flexDirection: "column", gap: 18 }}
      data-page="admin-odds-health"
    >
      <header style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <h1 style={{ margin: 0, fontSize: 24 }}>Odds Health</h1>
        <span style={eyebrow}>
          Founder-only. Every number here is a real count from the
          server. The credit floor (25) and warn threshold (100) are
          server-enforced — the UI cannot override them.
        </span>
      </header>

      {loading ? (
        <div style={card}>Loading…</div>
      ) : error ? (
        <div style={card} data-testid="odds-health-error">
          <span style={eyebrow}>Snapshot unavailable</span>
          <span>{error}</span>
        </div>
      ) : (
        <>
          {/* Credits */}
          <section style={card} data-testid="odds-health-credits">
            <span style={eyebrow}>The Odds API — credits remaining</span>
            <CreditBadge
              remaining={snapshot.credits.remaining}
              floor={snapshot.credits.floor}
              warn={snapshot.credits.warn_threshold}
            />
            <span style={{ fontSize: 11, color: "var(--sa-fg-muted)" }}>
              used: {snapshot.credits.used ?? "—"} · last seen:{" "}
              {fmtTs(snapshot.credits.last_seen_at)} · budget-blocks 24h:{" "}
              {snapshot.credits.budget_blocks_24h}
            </span>
          </section>

          {/* Escalations open */}
          <section style={card} data-testid="odds-health-escalations">
            <span style={eyebrow}>
              Open escalations · {snapshot.escalations_open.length} (24h:{" "}
              {snapshot.escalations_24h_count})
            </span>
            {snapshot.escalations_open.length === 0 ? (
              <span style={{ color: "var(--sa-fg-muted)" }}>
                No open escalations — honest empty.
              </span>
            ) : (
              snapshot.escalations_open.map((e) => (
                <div
                  key={e.id}
                  style={{ borderTop: "1px solid var(--sa-hairline, #2a2a30)", paddingTop: 8 }}
                  data-testid={`escalation-${e.id}`}
                >
                  <span style={{ ...cellMono, fontSize: 11, color: "var(--sa-accent-saffron)" }}>
                    {e.severity.toUpperCase()} · {e.reason}
                  </span>
                  <div style={{ ...cellMono, color: "var(--sa-fg-muted)" }}>
                    {e.endpoint ?? "—"} · {e.sport_key ?? "—"} ·{" "}
                    raised {fmtTs(e.raised_at)}
                  </div>
                  {e.notes ? <div style={cellMono}>{e.notes}</div> : null}
                </div>
              ))
            )}
          </section>

          {/* Data counts */}
          <section style={card} data-testid="odds-health-counts">
            <span style={eyebrow}>Data counts (the_odds_api / pinnacle)</span>
            <div style={{ ...cellMono, display: "grid", gridTemplateColumns: "1.5fr 1fr", gap: 6 }}>
              <span>fixture_markets rows</span>
              <span>{snapshot.fixture_markets.rows}</span>
              <span>distinct fixtures</span>
              <span>{snapshot.fixture_markets.fixtures}</span>
              <span>history rows (auto-mirrored)</span>
              <span>{snapshot.fixture_markets.history_rows}</span>
              <span>clv_pinnacle_benchmark</span>
              <span>{snapshot.benchmarks.pinnacle_rows}</span>
              <span>fixture_market_closing_lines (current)</span>
              <span>
                {snapshot.benchmarks.closing_lines_current}{" "}
                <span style={{ color: "var(--sa-fg-muted)" }}>
                  /{snapshot.benchmarks.closing_lines_rows} total
                </span>
              </span>
              <span>live_markets (gate)</span>
              <span>{snapshot.sport_coverage.live_markets}</span>
              <span>live_leagues (4-condition gate)</span>
              <span>{snapshot.sport_coverage.live_leagues}</span>
              <span>calibrating leagues</span>
              <span>{snapshot.sport_coverage.calibrating_leagues}</span>
            </div>
          </section>

          {/* Recent ledger */}
          <section style={card} data-testid="odds-health-ledger">
            <span style={eyebrow}>Credit ledger — last 10 calls</span>
            {snapshot.ledger_recent.length === 0 ? (
              <span style={{ color: "var(--sa-fg-muted)" }}>
                No ledger rows yet.
              </span>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {snapshot.ledger_recent.map((r, i) => (
                  <div
                    key={i}
                    style={{
                      ...cellMono,
                      display: "grid",
                      gridTemplateColumns: "1fr 0.6fr 1fr 0.6fr",
                      gap: 8,
                      borderBottom: "1px solid var(--sa-hairline, #2a2a30)",
                      padding: "4px 0",
                    }}
                  >
                    <span>{fmtTs(r.ran_at)}</span>
                    <span
                      style={{
                        color:
                          r.call_status === "ok"
                            ? "var(--sa-accent-positive)"
                            : r.call_status === "budget_block"
                            ? "var(--sa-accent-negative)"
                            : "var(--sa-accent-saffron)",
                      }}
                    >
                      {r.call_status}
                    </span>
                    <span>{r.endpoint}</span>
                    <span>
                      {r.requests_remaining ?? "—"}
                      {r.http_status !== null ? ` · http ${r.http_status}` : ""}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Next kickoffs */}
          <section style={card} data-testid="odds-health-kickoffs">
            <span style={eyebrow}>
              Next kickoffs with Pinnacle odds — {snapshot.next_kickoffs.length}
            </span>
            {snapshot.next_kickoffs.length === 0 ? (
              <span style={{ color: "var(--sa-fg-muted)" }}>
                No upcoming fixtures with the_odds_api+pinnacle rows.
              </span>
            ) : (
              snapshot.next_kickoffs.map((k) => (
                <div
                  key={k.fixture_id}
                  style={{
                    ...cellMono,
                    display: "grid",
                    gridTemplateColumns: "2fr 1fr",
                    gap: 8,
                    borderTop: "1px solid var(--sa-hairline, #2a2a30)",
                    padding: "4px 0",
                  }}
                >
                  <span>{k.fixture_id}</span>
                  <span style={{ color: "var(--sa-fg-muted)" }}>{fmtTs(k.kickoff_at)}</span>
                </div>
              ))
            )}
          </section>

          <button
            type="button"
            onClick={() => void refetch()}
            style={{
              alignSelf: "flex-start",
              padding: "6px 12px",
              background: "transparent",
              color: "var(--sa-accent-positive, #1fbf75)",
              border: "1px solid var(--sa-accent-positive, #1fbf75)",
              borderRadius: 3,
              fontFamily: "var(--sa-font-mono, monospace)",
              fontSize: 11,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              cursor: "pointer",
            }}
          >
            Refresh
          </button>
        </>
      )}
    </div>
  );
}
